import { z } from 'zod';
import { isPastInputValue, toDateInputValue } from '@/shared/utils/date-input';
import { dateFieldSchema } from '../../logic/date-field';

/**
 * Schema for the DRAFT campaign edit form. Covers the fields the backend's
 * `PUT /campaigns/:id` endpoint accepts — intentionally narrower than the
 * creation wizard because creatives/objective/platform can't be changed
 * after a campaign has been persisted.
 */
export const editCampaignSchema = z
  .object({
    name: z
      .string()
      .min(3, 'Campaign name must be at least 3 characters')
      .max(100, 'Campaign name must be at most 100 characters'),
    startDate: dateFieldSchema('Start date'),
    endDate: dateFieldSchema('End date'),
    dailyBudgetCents: z
      .number()
      .int('Daily budget must be a whole number of cents')
      .min(100, 'Daily budget must be at least $1.00'),
    totalBudgetCents: z
      .number()
      .int('Total budget must be a whole number of cents')
      .min(100, 'Total budget must be at least $1.00'),
    ageMin: z.number().int().min(13, 'Minimum age is 13').max(65, 'Max age must not exceed 65'),
    ageMax: z.number().int().min(13, 'Minimum age is 13').max(65, 'Max age must not exceed 65'),
    genders: z.array(z.enum(['male', 'female', 'all'])),
    keywords: z.string().optional(),
    pixelId: z.string().optional(),
    conversionEvent: z.string().optional(),
    landingUrl: z.string().optional(),
  })
  .refine(
    (data) => {
      const start = toDateInputValue(data.startDate);
      const end = toDateInputValue(data.endDate);
      // An unparseable date is already reported on the field itself; comparing
      // it here would stack a bogus "end before start" on top of that error.
      if (!start || !end) return true;
      return end > start;
    },
    { message: 'End date must be after start date', path: ['endDate'] },
  )
  // Same rule as the creation wizard: no platform accepts a flight that already
  // started. Unconditional here because CampaignEditPage only renders the form
  // for DRAFT campaigns — a RUNNING one, whose start legitimately sits in the
  // past, gets the read-only view instead.
  .refine((data) => !isPastInputValue(data.startDate), {
    message: "Start date can't be in the past",
    path: ['startDate'],
  })
  .refine((data) => data.ageMax >= data.ageMin, {
    message: 'Max age must be greater than or equal to min age',
    path: ['ageMax'],
  })
  .refine(
    (data) => {
      if (!data.landingUrl) return true;
      try {
        return new URL(data.landingUrl).protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'Landing URL must be a valid HTTPS URL', path: ['landingUrl'] },
  );

export type EditCampaignFormValues = z.infer<typeof editCampaignSchema>;
